import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PhoneFill from '@/assets/icons/phone_fill.svg?component';
import { CardHeader } from '@/components/atoms/Card';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import { renderMarkdown } from '@/utils/markdown';

import './Help.scss';

/*
 * Help page text is stored in translations item: helpPageDetail
 * This item can be Object, List or string.
 *
 * In case it is Object it is treated as some kind of section that
 * may contain title, phone, email, and manual  information. First Object will have
 * level=1, second (child) object will have level2 and so on.
 * Every level will be shown as <h1>, <h2> and so on respectively.
 * If title is not set, Object will not show <h1> but just others.
 * Object may contain extra fields, currently `textAlign` supported.
 *
 * List contains list (sic) of items.
 *
 * string is treted as markdown. Extra params from Object may be
 * passed to string to do extra formatting, ex. `textAlign`.
 *
 * In most of the cases you want one or list of strings.
 * Maybe object if you need extra formating.
 *
 * If you ever need to change some default formatting,
 * wrap everyting into styled componetnt's div.
 */

interface HelpItemProps {
    /** A piece of the `help.pageDetail.contacts` translation tree. */
    item: unknown;
    level?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function HelpPageItemList({ item, level = 1 }: { item: unknown[]; level?: number }) {
    return (
        <>
            {item.map((v, index) => (
                <HelpPageItem key={index} item={v} level={level} />
            ))}
        </>
    );
}

function HelpPageItemObject({
    item,
    level = 1,
}: {
    item: Record<string, unknown>;
    level?: number;
}) {
    const { title, ...extra } = item;

    return (
        <>
            {!!title && (
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                <span
                    dangerouslySetInnerHTML={{
                        __html: `<h${level} class="captionWithUppercase">${title}</h${level}>`,
                    }}
                />
            )}
            {Object.values(extra).map((value, index) => (
                <HelpPageItem key={index} item={value} level={level + 1} />
            ))}
        </>
    );
}

function HelpPageItemString({ item }: { item: string }) {
    const output = useMemo(() => renderMarkdown(item), [item]);
    return <span dangerouslySetInnerHTML={{ __html: output }} />;
}

function HelpPageItem({ item, level = 1 }: HelpItemProps) {
    if (Array.isArray(item)) {
        return <HelpPageItemList item={item} level={level} />;
    }
    if (isRecord(item)) {
        return <HelpPageItemObject item={item} level={level} />;
    }
    return <HelpPageItemString item={item == null ? '' : String(item)} />;
}

function HelpPageDetail() {
    const { t } = useTranslation();
    const help = t('help.pageDetail.contacts', { returnObjects: true });

    return (
        <div data-type="helpPageContactSection">
            <CardHeader
                title={t('help.pageDetail.contactInformation')}
                level="h2"
                size="card"
                icon={<TitleIcon icon={PhoneFill} />}
            />
            <HelpPageItem item={help} level={2} />
        </div>
    );
}

export default HelpPageDetail;
